// (c) chaehongee. Confidential and proprietary.

import { useCallback } from 'react'
import { exportAndUploadFrame, type ExportFrameResult } from '../../../../services/frameExport'
import type { FrameEdit, Rect } from './types'

type Size = { w: number; h: number }

export interface UseExportFramedImageWithUploadArgs {
    nodeId: string | null
    sourceUrl: string | null
    imageNaturalSize: Size | null
    frameEditStateRef: React.RefObject<Record<string, FrameEdit>>
    imageRectInContainer: Rect | null
    initialWorldRectsById: Record<string, Rect>
    imageSizesBySrc: Record<string, Size | undefined>
    sessionId: string | null
    hasDrawingChanges?: boolean
    getDrawingOverlay?: () => Promise<Blob | null>
}

/**
 * Hook that exports a framed image and uploads it with commit tracking.
 * This is used by the Backstage module to export frames when sending chat messages.
 * 
 * Returns null if no changes detected, otherwise returns the uploaded node and commit.
 */
export function useExportFramedImageWithUpload(
    args: UseExportFramedImageWithUploadArgs
): () => Promise<ExportFrameResult | null> {
    const {
        nodeId,
        imageNaturalSize,
        frameEditStateRef,
        initialWorldRectsById,
        imageSizesBySrc,
        sessionId,
        hasDrawingChanges = false,
        getDrawingOverlay,
    } = args

    return useCallback(async () => {
        if (!nodeId || !sessionId) return null
        
        const edit = frameEditStateRef.current?.[nodeId]
        if (!edit || !edit.layers || edit.layers.length === 0) return null

        const primaryLayer = edit.layers.find(l => l.id === nodeId) || edit.layers[0]
        const imageRect = primaryLayer.currentRect
        const frameRect = edit.currentFrame

        // Check if there are any changes
        const unchanged =
            Math.abs(frameRect.x - imageRect.x) < 0.5 &&
            Math.abs(frameRect.y - imageRect.y) < 0.5 &&
            Math.abs(frameRect.w - imageRect.w) < 0.5 &&
            Math.abs(frameRect.h - imageRect.h) < 0.5

        if (unchanged && !hasDrawingChanges) return null

        // Get drawing overlay if available (only for primary layer)
        const overlayBlob = getDrawingOverlay ? await getDrawingOverlay() : null

        // Export ALL layers in the frame (not just the primary one!)
        const layersToExport = await Promise.all(edit.layers.map(async (layer) => {
            const natural = imageSizesBySrc[layer.src] || imageNaturalSize
            if (!natural) throw new Error(`Missing natural size for layer ${layer.src}`)
            
            // Only the primary layer gets the drawing overlay
            const layerOverlay = (layer.id === nodeId) ? overlayBlob : null

            return {
                id: layer.id,
                sourceUrl: layer.src,
                imageRect: layer.currentRect,
                frameRect: edit.currentFrame,
                imageNaturalSize: natural,
                initialImageRect: layer.baseRect,
                zIndex: layer.zIndex,
                overlayBlob: layerOverlay,
            }
        }))

        // Export and upload using shared service
        const result = await exportAndUploadFrame({
            sessionId,
            frameRect: edit.currentFrame,
            layers: layersToExport,
            commitMessage: 'Edited: frame resize',
            baseNodeIds: [nodeId],
        })

        return result
    }, [
        nodeId,
        imageNaturalSize,
        sessionId,
        initialWorldRectsById,
        imageSizesBySrc,
        frameEditStateRef,
        hasDrawingChanges,
        getDrawingOverlay,
    ])
}
